// Cyberpunk-terminal UI theme (docs/ART-DIRECTION.md §1, §3, §4) for the HUD and the flow screens. Colours are
// written as CSS custom properties from TypeScript, so the stylesheets carry no colour literals: every hue comes from
// the palette (render/palette.ts, the presentation tuning's colour roles), and a new palette is one call.

import './theme.css'
import type { ColourRole } from '@taxwar/tuning'
import { paletteOf, type Palette } from '../render/palette'
import type { VisualSettings } from '../render/visualSettings'
import type { TuningLink } from '../tuning/link'

/** Root class of every themed DOM tree (HUD root, flow screen overlays). */
export const UI_ROOT_CLASS = 'tw-ui'
const REDUCE_EFFECTS_CLASS = 'tw-reduce-effects'

/** The custom property of each palette role the stylesheets and the ledger's canvas use (the rest are drawn in 3D). */
const ROLE_PROPERTIES: Readonly<Partial<Record<ColourRole, string>>> = {
  player: '--tw-player',
  enemy: '--tw-enemy',
  neutral: '--tw-neutral',
  money: '--tw-money',
  warning: '--tw-warning',
  danger: '--tw-danger',
  text: '--tw-text',
  textDim: '--tw-text-dim',
  frame: '--tw-frame',
  background: '--tw-bg',
  globeDepth: '--tw-bg-deep',
}
const THEMED_ROLES = Object.keys(ROLE_PROPERTIES) as readonly ColourRole[]

/** Applies the palette and the effects setting to a themed root; call again on every palette or settings change. */
export function applyUiTheme(root: HTMLElement, palette: Palette, settings: VisualSettings): void {
  root.classList.add(UI_ROOT_CLASS)
  const style = root.style
  for (const role of THEMED_ROLES) style.setProperty(ROLE_PROPERTIES[role] as string, `#${palette[role].getHexString()}`)
  root.classList.toggle(REDUCE_EFFECTS_CLASS, settings.reduceEffects)
}

/** A flow screen's theme: the tuning's colours in the player's palette, with the player's effects setting. */
export function applyScreenTheme(root: HTMLElement, tuning: TuningLink, settings: VisualSettings): void {
  applyUiTheme(root, paletteOf(tuning.values, settings.palette), settings)
}
